// components/ui/calendar.tsx
import * as React from "react"
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react"
import {
  DateRange,
  DayPicker,
  SelectMultipleEventHandler,
  SelectRangeEventHandler,
  SelectSingleEventHandler,
} from "react-day-picker"

import { cn } from "@/lib/utils"

import { addDays, isAfter } from "date-fns"

export type CalendarProps = Omit<
  React.ComponentProps<typeof DayPicker>,
  "mode" | "onSelect" | "selected"
> & {
  range?: DateRange
  onRangeChange?: (range: DateRange | undefined) => void
  onConfirm?: (range: DateRange | undefined) => void
  onCancel?: () => void
  numberOfMonths?: number
}

export function CalendarRange({
  className,
  classNames,
  showOutsideDays = true,
  range,
  onRangeChange,
  onConfirm,
  onCancel,
  numberOfMonths = 2,
  ...props
}: CalendarProps) {
  const [internalRange, setInternalRange] = React.useState<
    DateRange | undefined
  >(range)
  const [fromMonth, setFromMonth] = React.useState<Date>(
    range?.from ?? new Date(),
  )
  const [toMonth, setToMonth] = React.useState<Date>(
    range?.to ?? new Date(new Date().setMonth(new Date().getMonth() + 1)),
  )

  // Sync internal state with props
  React.useEffect(() => {
    setInternalRange(range)
    if (range?.from) setFromMonth(range.from)
    if (range?.to) setToMonth(range.to)
  }, [range])

  const handleFromMonthChange = (date: Date) => {
    setFromMonth(date)
  }

  const handleToMonthChange = (date: Date) => {
    setToMonth(date)
  }

  const handleDateSelect: SelectRangeEventHandler = (
    selectedRange: DateRange | undefined,
  ) => {
    setInternalRange(selectedRange)
    onRangeChange?.(selectedRange)
  }

  const handleConfirmClick = () => {
    if (onConfirm) onConfirm(internalRange)
  }

  const handleCancelClick = () => {
    if (onCancel) onCancel()
  }

  const handlePrevMonth = (type: "from" | "to") => {
    if (type === "from") {
      const prevMonth = new Date(fromMonth)
      prevMonth.setMonth(prevMonth.getMonth() - 1)
      setFromMonth(prevMonth)
    } else {
      const prevMonth = new Date(toMonth)
      prevMonth.setMonth(prevMonth.getMonth() - 1)
      setToMonth(prevMonth)
    }
  }

  const handleNextMonth = (type: "from" | "to") => {
    if (type === "from") {
      const nextMonth = new Date(fromMonth)
      nextMonth.setMonth(nextMonth.getMonth() + 1)
      setFromMonth(nextMonth)
    } else {
      const nextMonth = new Date(toMonth)
      nextMonth.setMonth(nextMonth.getMonth() + 1)
      setToMonth(nextMonth)
    }
  }

  return (
    <div className="flex w-full max-w-[760px] flex-col space-y-4 rounded-lg bg-white p-4 shadow-md dark:bg-gray-900 dark:shadow-gray-800">
      <div className="flex items-center justify-between pb-4">
        <h3 className="text-lg font-semibold dark:text-white">Select dates</h3>
        <p className="text-sm text-muted-foreground dark:text-gray-400">
          Choose dates for viewing.
        </p>
      </div>

      <div className="flex flex-col gap-4 md:flex-row md:gap-6">
        {/* FROM calendar - Check-in */}
        <div className="flex-1">
          <div className="mb-2 flex space-x-3">
            <div className="relative w-full">
              <select
                value={fromMonth.getMonth()}
                onChange={(e) => {
                  const month = parseInt(e.target.value)
                  const newDate = new Date(fromMonth)
                  newDate.setMonth(month)
                  handleFromMonthChange(newDate)
                }}
                className="w-full appearance-none rounded-md border px-4 py-2 pr-10 text-sm focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-white"
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i} value={i} className="dark:bg-gray-800">
                    {new Date(0, i).toLocaleString("default", {
                      month: "long",
                    })}
                  </option>
                ))}
              </select>
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 transform text-gray-500 dark:text-gray-400">
                <ChevronDown className="h-4 w-4" />
              </span>
            </div>
            <div className="relative w-full">
              <select
                value={fromMonth.getFullYear()}
                onChange={(e) => {
                  const year = parseInt(e.target.value)
                  const newDate = new Date(fromMonth)
                  newDate.setFullYear(year)
                  handleFromMonthChange(newDate)
                }}
                className="w-full appearance-none rounded-md border px-4 py-2 pr-10 text-sm focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-white"
              >
                {Array.from(
                  { length: 20 },
                  (_, i) => new Date().getFullYear() - 10 + i,
                ).map((year) => (
                  <option key={year} value={year} className="dark:bg-gray-800">
                    {year}
                  </option>
                ))}
              </select>
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 transform text-gray-500 dark:text-gray-400">
                <ChevronDown className="h-4 w-4" />
              </span>
            </div>
          </div>
          <div className="flex items-start gap-1">
            <button
              onClick={() => handlePrevMonth("from")}
              className="self-center rounded-md p-2 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <ChevronLeft className="h-4 w-4 dark:text-white" />
            </button>
            <DayPicker
              mode="range"
              selected={internalRange}
              onSelect={handleDateSelect}
              month={fromMonth}
              onMonthChange={handleFromMonthChange}
              numberOfMonths={1}
              showOutsideDays={showOutsideDays}
              disabled={(date) => isAfter(date, new Date())}
              className={cn("p-0", className)}
              classNames={{
                ...classNames,
                caption: "hidden",
                caption_label: "hidden",
                nav: "hidden",
                nav_button: "hidden",
                nav_button_previous: "hidden",
                nav_button_next: "hidden",
                head_row: "grid grid-cols-7 gap-1 mb-2",
                head_cell:
                  "text-muted-foreground dark:text-gray-400 rounded-md w-9 h-9 font-bold text-sm mt-[2px]",
                row: "grid grid-cols-7 gap-1",
                cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20",
                day: cn(
                  "h-9 w-9 p-0 font-normal aria-selected:opacity-100 rounded-md flex items-center justify-center",
                  "hover:bg-gray-100 dark:hover:bg-gray-800",
                  "focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 dark:focus:ring-gray-600",
                  "dark:text-white",
                ),
                day_range_middle:
                  "aria-selected:bg-teal-100 aria-selected:text-teal-900 dark:aria-selected:bg-teal-900/50 dark:aria-selected:text-teal-100",
                day_selected:
                  "bg-teal-600 text-white hover:bg-teal-700 hover:text-white dark:bg-teal-700 dark:hover:bg-teal-800",
                day_outside:
                  "text-muted-foreground opacity-50 dark:text-gray-400",
                day_disabled:
                  "text-muted-foreground opacity-50 dark:text-gray-400",
                day_hidden: "invisible",
              }}
            />
            <button
              onClick={() => handleNextMonth("from")}
              className="self-center rounded-md p-2 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <ChevronRight className="h-4 w-4 dark:text-white" />
            </button>
          </div>
        </div>

        {/* TO calendar - Check-out */}
        <div className="flex-1">
          <div className="mb-2 flex space-x-3">
            <div className="relative w-full">
              <select
                value={toMonth.getMonth()}
                onChange={(e) => {
                  const month = parseInt(e.target.value)
                  const newDate = new Date(toMonth)
                  newDate.setMonth(month)
                  handleToMonthChange(newDate)
                }}
                className="w-full appearance-none rounded-md border px-4 py-2 pr-10 text-sm focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-white"
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i} value={i} className="dark:bg-gray-800">
                    {new Date(0, i).toLocaleString("default", {
                      month: "long",
                    })}
                  </option>
                ))}
              </select>
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 transform text-gray-500 dark:text-gray-400">
                <ChevronDown className="h-4 w-4" />
              </span>
            </div>
            <div className="relative w-full">
              <select
                value={toMonth.getFullYear()}
                onChange={(e) => {
                  const year = parseInt(e.target.value)
                  const newDate = new Date(toMonth)
                  newDate.setFullYear(year)
                  handleToMonthChange(newDate)
                }}
                className="w-full appearance-none rounded-md border px-4 py-2 pr-10 text-sm focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-white"
              >
                {Array.from(
                  { length: 20 },
                  (_, i) => new Date().getFullYear() - 10 + i,
                ).map((year) => (
                  <option key={year} value={year} className="dark:bg-gray-800">
                    {year}
                  </option>
                ))}
              </select>
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 transform text-gray-500 dark:text-gray-400">
                <ChevronDown className="h-4 w-4" />
              </span>
            </div>
          </div>
          <div className="flex items-start gap-1">
            <button
              onClick={() => handlePrevMonth("to")}
              className="self-center rounded-md p-2 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <ChevronLeft className="h-4 w-4 dark:text-white" />
            </button>
            {/* TO calendar - Check-out */}
<DayPicker
mode="range"
selected={internalRange}
onSelect={handleDateSelect}
month={toMonth} 
onMonthChange={handleToMonthChange} 
numberOfMonths={1}
showOutsideDays={showOutsideDays}
disabled={(date) => isAfter(date, new Date())} 
className={cn("p-0", className)}
classNames={{
  ...classNames,
  caption: "hidden",
  caption_label: "hidden",
  nav: "hidden",
  nav_button: "hidden",
  nav_button_previous: "hidden",
  nav_button_next: "hidden",
  head_row: "grid grid-cols-7 gap-1 mb-2",
  head_cell:
    "text-muted-foreground dark:text-gray-400 rounded-md w-9 h-9 font-bold text-sm mt-[2px]",
  row: "grid grid-cols-7 gap-1",
  cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20",
  day: cn(
    "h-9 w-9 p-0 font-normal aria-selected:opacity-100 rounded-md flex items-center justify-center",
    "hover:bg-gray-100 dark:hover:bg-gray-800",
    "focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 dark:focus:ring-gray-600",
    "dark:text-white",
  ),
  day_range_middle:
    "aria-selected:bg-teal-100 aria-selected:text-teal-900 dark:aria-selected:bg-teal-900/50 dark:aria-selected:text-teal-100",
  day_selected:
    "bg-teal-600 text-white hover:bg-teal-700 hover:text-white dark:bg-teal-700 dark:hover:bg-teal-800",
  day_outside:
    "text-muted-foreground opacity-50 dark:text-gray-400",
  day_disabled:
    "text-muted-foreground opacity-50 dark:text-gray-400",
  day_hidden: "invisible",
}}
/>
            <button
              onClick={() => handleNextMonth("to")}
              className="self-center rounded-md p-2 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <ChevronRight className="h-4 w-4 dark:text-white" />
            </button>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between pt-6">
        <div className="text-sm">
          {internalRange?.from && internalRange?.to ? (
            <div className="flex items-center space-x-2">
              <span className="rounded-full bg-teal-100 px-4 py-2 font-medium text-teal-900 dark:bg-teal-900/50 dark:text-teal-100">
                {internalRange.from.toLocaleDateString()}
              </span>
              <span className="text-muted-foreground dark:text-gray-400">
                to
              </span>
              <span className="rounded-full bg-teal-100 px-4 py-2 font-medium text-teal-900 dark:bg-teal-900/50 dark:text-teal-100">
                {internalRange.to.toLocaleDateString()}
              </span>
            </div>
          ) : (
            <div className="italic text-muted-foreground dark:text-gray-400">
              No dates selected
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2">
          <button
            className="rounded-md border px-4 py-2 text-sm transition-colors hover:bg-gray-100 dark:border-gray-700 dark:text-white dark:hover:bg-gray-800"
            onClick={handleCancelClick}
          >
            Cancel
          </button>
          <button
            className="rounded-md bg-teal-600 px-4 py-2 text-sm text-white transition-colors hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-teal-800"
            onClick={handleConfirmClick}
            disabled={!internalRange?.from || !internalRange?.to}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  )
}
